/**
 * Step 4 — Parameters.
 *
 * Parameters are the control surface of a GI: kind-tagged scalar
 * values with optional ranges. The form renders them as an editable
 * list; each row self-validates against the declared kind (integer /
 * float / bool / string / enum).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';

import { parameterKindSchema, step4Schema, type Step4Values } from '../schemas';
import { useSparkStore } from '../store';
import { Field, inputClass, WizardFooter } from './Step1Basic';

const KINDS = parameterKindSchema.options;

export function Step4Parameters({
  onAdvance,
  onBack,
}: {
  onAdvance: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('spark');
  const parameters = useSparkStore((s) => s.parameters);

  const form = useForm<Step4Values>({
    resolver: zodResolver(step4Schema),
    mode: 'onBlur',
    defaultValues: { parameters },
  });
  const { control, register, formState, handleSubmit } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'parameters' });
  const paramErrors = formState.errors.parameters ?? [];

  const onSubmit = (v: Step4Values) => {
    useSparkStore.getState().applyStep4(v);
    onAdvance();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <p className="text-sm text-ink-muted">{t('steps.4.intro')}</p>

      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-surface p-4 text-sm text-ink-muted">
          {t('steps.4.empty')}
        </p>
      ) : null}

      {fields.map((f, i) => {
        const err = (paramErrors as Record<string, never>[])[i] ?? {};
        const e = err as Record<string, { message?: string } | undefined>;
        return (
          <div
            key={f.id}
            className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface p-4 md:grid-cols-[1fr_160px_1fr_auto]"
          >
            <Field label={t('params.field_name')} error={e.name?.message}>
              <input
                type="text"
                {...register(`parameters.${i}.name`)}
                className={inputClass(Boolean(e.name))}
                placeholder="threshold"
              />
            </Field>

            <Field label={t('params.field_kind')} error={e.kind?.message}>
              <select
                {...register(`parameters.${i}.kind`)}
                className={inputClass(Boolean(e.kind))}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={t('params.field_default')}
              hint={t('params.default_hint')}
              error={e.default_text?.message}
            >
              <input
                type="text"
                {...register(`parameters.${i}.default_text`)}
                className={inputClass(Boolean(e.default_text))}
                placeholder="0.75"
              />
            </Field>

            <div className="flex items-end">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => remove(i)}
              >
                {t('ports.remove_short')}
              </Button>
            </div>

            {/* Range / enum row — kind-conditional. We always render
                the fields so the schema can re-evaluate on change. */}
            <Field label={t('params.field_min')} error={e.range_min?.message}>
              <input
                type="text"
                {...register(`parameters.${i}.range_min`)}
                className={inputClass(Boolean(e.range_min))}
                placeholder="(optional)"
              />
            </Field>
            <Field label={t('params.field_max')} error={e.range_max?.message}>
              <input
                type="text"
                {...register(`parameters.${i}.range_max`)}
                className={inputClass(Boolean(e.range_max))}
                placeholder="(optional)"
              />
            </Field>
            <Field
              label={t('params.field_enum')}
              hint={t('params.enum_hint')}
              error={e.enum_variants?.message}
            >
              <input
                type="text"
                {...register(`parameters.${i}.enum_variants`)}
                className={inputClass(Boolean(e.enum_variants))}
                placeholder="low, medium, high"
              />
            </Field>
            <div />

            <div className="md:col-span-4">
              <Field
                label={t('params.field_description')}
                error={e.description?.message}
              >
                <input
                  type="text"
                  {...register(`parameters.${i}.description`)}
                  className={inputClass(false)}
                  placeholder={t('params.description_placeholder')}
                />
              </Field>
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          append({
            name: '',
            description: '',
            kind: 'Float',
            default_text: '0',
            range_min: '',
            range_max: '',
            enum_variants: '',
          })
        }
      >
        {t('params.add')}
      </Button>

      <WizardFooter onBack={onBack}>
        <Button type="submit" variant="primary">
          {t('actions.next', { ns: 'common' })}
        </Button>
      </WizardFooter>
    </form>
  );
}
